import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContextWeb } from "../../context/AuthContextWeb";
import { apiRequest } from "../../lib/apiClient";
import { Building2, CheckCircle2, Eye, Pencil, Trash2 } from "lucide-react";

export default function OwnerDashboardPage() {
  const { user } = useContext(AuthContextWeb);
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchProperties = useCallback(async () => {
    if (!user?.userId) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest(`/properties/owner/${user.userId}`);
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Failed to load properties.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await apiRequest(`/properties/${id}`, { method: "DELETE" });
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err?.message || "Failed to delete property.");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const totalListings = properties.length;
  const activeListings = properties.filter(
    (p) => p.status !== "INACTIVE",
  ).length;

  return (
    <div className="owner-page">
      <div className="owner-page-header">
        <h1 className="owner-page-title">
          Welcome back, {user?.name?.split(" ")[0] || "Owner"}
        </h1>
        <p className="owner-page-subtitle">
          Here's an overview of your rental portfolio.
        </p>
      </div>

      {/* Stats row */}
      <div className="owner-stats-grid">
        <div className="owner-stat-card">
          <Building2 size={52} strokeWidth={1} className="owner-stat-bg-icon" />
          <p className="owner-stat-value">
            {loading ? "\u2014" : totalListings}
          </p>
          <p className="owner-stat-label">Total Listings</p>
        </div>
        <div className="owner-stat-card">
          <CheckCircle2
            size={52}
            strokeWidth={1}
            className="owner-stat-bg-icon"
          />
          <p className="owner-stat-value">{loading ? "—" : activeListings}</p>
          <p className="owner-stat-label">Active</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="owner-quick-actions">
        <button
          type="button"
          className="owner-action-btn owner-action-btn--primary"
          onClick={() => navigate("/owner/add-property")}
        >
          Add New Property
        </button>
      </div>

      {/* Recent listings */}
      <section className="owner-section">
        <h2 className="owner-section-title">Your Listings</h2>

        {error && <p className="form-error">{error}</p>}

        {loading ? (
          <p className="loading-message">Loading your properties…</p>
        ) : properties.length === 0 ? (
          <div className="owner-empty-state">
            <p className="owner-empty-text">
              You haven't listed any properties yet.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate("/owner/add-property")}
            >
              Add your first property
            </button>
          </div>
        ) : (
          <div className="owner-listings-grid">
            {properties.map((p) => (
              <OwnerPropertyCard
                key={p.id}
                property={p}
                onEdit={() => navigate(`/owner/edit-property/${p.id}`)}
                onView={() => navigate(`/owner/properties/${p.id}`)}
                onDelete={() => setConfirmDeleteId(p.id)}
                isDeleting={deletingId === p.id}
                confirmingDelete={confirmDeleteId === p.id}
                onConfirmDelete={() => handleDelete(p.id)}
                onCancelDelete={() => setConfirmDeleteId(null)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function OwnerPropertyCard({
  property,
  onEdit,
  onView,
  onDelete,
  isDeleting,
  confirmingDelete,
  onConfirmDelete,
  onCancelDelete,
}) {
  const cover = property.images?.[0]?.url;
  const price =
    property.price != null
      ? new Intl.NumberFormat("en-NP", {
          style: "currency",
          currency: "NPR",
          maximumFractionDigits: 0,
        }).format(property.price) + "/mo"
      : null;

  return (
    <div className="owner-prop-card">
      <div className="owner-prop-img-wrap">
        {cover ? (
          <img src={cover} alt={property.title} className="owner-prop-img" />
        ) : (
          <div className="owner-prop-img-placeholder">&#128247;</div>
        )}
      </div>
      <div className="owner-prop-body">
        <p className="owner-prop-title">{property.title || "Untitled"}</p>
        {(property.location || property.city) && (
          <p className="owner-prop-location">
            {property.location || property.city}
          </p>
        )}
        {price && <p className="owner-prop-price">{price}</p>}
      </div>
      <div className="owner-prop-footer">
        {confirmingDelete ? (
          <div className="owner-prop-confirm-row">
            <span className="owner-prop-confirm-text">
              Delete this listing?
            </span>
            <button
              type="button"
              className="owner-prop-icon-btn owner-prop-icon-btn--danger-solid"
              onClick={onConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "\u2026" : "Yes"}
            </button>
            <button
              type="button"
              className="owner-prop-icon-btn"
              onClick={onCancelDelete}
              disabled={isDeleting}
            >
              No
            </button>
          </div>
        ) : (
          <div className="owner-prop-icon-btns">
            <div className="owner-prop-icon-btns-group">
              <button
                type="button"
                className="owner-prop-icon-btn"
                onClick={onView}
                title="View listing"
              >
                <Eye size={20} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                className="owner-prop-icon-btn"
                onClick={onEdit}
                title="Edit listing"
              >
                <Pencil size={20} strokeWidth={1.75} />
              </button>
            </div>
            <button
              type="button"
              className="owner-prop-icon-btn owner-prop-icon-btn--danger"
              onClick={onDelete}
              title="Delete listing"
            >
              <Trash2 size={20} strokeWidth={1.75} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
